import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/MainPage.css';
import { useAuth } from '../context/AuthContext';
import { getPopularCampaigns, getAnbefaletCampaigns, getCampaignsByCategory, mergeCampaignsWithCompletedStatus } from '../services/campaignService';
import { getUserProfile, getUserIconPath } from '../services/userService';
import { Campaign } from '../types/campaign';
import Footer from '../components/Footer';
import FriendRequestNotification from '../components/FriendRequestNotification';
import PrizeReceivedNotification from '../components/PrizeReceivedNotification';
import { getPendingFriendRequests } from '../services/friendService';
import { getPrizeNotifications } from '../services/prizeShareService';
import { FriendRequest } from '../types/friendRequest';
import { PrizeNotification } from '../types/prizeNotification';

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [userIconPath, setUserIconPath] = useState<string>('/images/User_icon.png');
  const [popularCampaigns, setPopularCampaigns] = useState<Campaign[]>([]);
  const [recommendedCampaigns, setRecommendedCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Notification states
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [prizeNotifications, setPrizeNotifications] = useState<{ [senderId: string]: PrizeNotification[] }>({});
  const [showFriendRequestNotification, setShowFriendRequestNotification] = useState(false);
  const [showPrizeNotification, setShowPrizeNotification] = useState(false);
  const [notificationsChecked, setNotificationsChecked] = useState(false);

  useEffect(() => {
    // Get the user's first name and icon
    if (currentUser) {
      if (currentUser.displayName) {
        const firstName = currentUser.displayName.split(' ')[0];
        setUserName(firstName);
      }

      // Fetch user profile for icon
      const fetchUserIcon = async () => {
        try {
          const profile = await getUserProfile(currentUser.uid);
          if (profile) {
            setUserIconPath(getUserIconPath(profile.userIcon));
          }
        } catch (error) {
          console.error('Error fetching user icon:', error);
        }
      };

      fetchUserIcon();
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      
      if (selectedCategory) {
        // When a category is selected, fetch campaigns by category
        const categoryData = await getCampaignsByCategory(selectedCategory);
        // Merge with completed status
        const categoryDataWithCompleted = await mergeCampaignsWithCompletedStatus(
          categoryData,
          currentUser?.uid
        );
        // Split them based on first 3 and rest (old behavior for category filtering)
        setPopularCampaigns(categoryDataWithCompleted.slice(0, 3));
        setRecommendedCampaigns(categoryDataWithCompleted.slice(3));
      } else {
        // When no category is selected, fetch by mainCategory
        const [popular, anbefalet] = await Promise.all([
          getPopularCampaigns(),
          getAnbefaletCampaigns()
        ]);
        // Merge both with completed status
        const [popularWithCompleted, anbefaletWithCompleted] = await Promise.all([
          mergeCampaignsWithCompletedStatus(popular, currentUser?.uid),
          mergeCampaignsWithCompletedStatus(anbefalet, currentUser?.uid)
        ]);
        setPopularCampaigns(popularWithCompleted);
        setRecommendedCampaigns(anbefaletWithCompleted);
      }
      
      setLoading(false);
    };

    fetchCampaigns();
  }, [selectedCategory, currentUser]);

  // Load notifications on mount
  useEffect(() => {
    const loadNotifications = async () => {
      if (!currentUser || notificationsChecked) return;

      try {
        const [requests, prizes] = await Promise.all([
          getPendingFriendRequests(currentUser.uid),
          getPrizeNotifications(currentUser.uid)
        ]);

        setFriendRequests(requests);
        setPrizeNotifications(prizes);

        // Show notifications in sequence: friend requests first, then prizes
        if (requests.length > 0) {
          setShowFriendRequestNotification(true);
        } else if (Object.keys(prizes).length > 0) {
          setShowPrizeNotification(true);
        }

        setNotificationsChecked(true);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setNotificationsChecked(true);
      }
    };

    loadNotifications();
  }, [currentUser, notificationsChecked]);

  const handleFriendRequestUpdate = async () => {
    if (!currentUser) return;
    
    try {
      const requests = await getPendingFriendRequests(currentUser.uid);
      setFriendRequests(requests);
      
      // If no more requests, close the notification
      if (requests.length === 0) {
        setShowFriendRequestNotification(false);
        
        // Show prize notifications if any
        if (Object.keys(prizeNotifications).length > 0) {
          setShowPrizeNotification(true);
        }
      }
    } catch (error) {
      console.error('Error updating friend requests:', error);
    }
  };

  const handleCloseFriendRequestNotification = () => {
    setShowFriendRequestNotification(false);
    
    // Show prize notifications if any
    if (Object.keys(prizeNotifications).length > 0) {
      setShowPrizeNotification(true);
    }
  };

  const handleClosePrizeNotification = () => {
    setShowPrizeNotification(false);
  };

  // Categories array with images and text
  const categories = [
    { id: 1, name: 'Mad', image: '/images/sli-1.png' },
    { id: 2, name: 'Sport', image: '/images/sli-2.png' },
    { id: 3, name: 'Beauty', image: '/images/sli-3.png' },
    { id: 4, name: 'Tøj', image: '/images/sli-4.png' },
    { id: 5, name: 'Musik', image: '/images/sli-5.png' },
    { id: 6, name: 'Film', image: '/images/sli-6.png' }
  ];

  const handleStartGame = (campaignId: string) => {
    navigate(`/game/${campaignId}`);
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(selectedCategory === categoryName ? null : categoryName);
  };

  const handleProfileClick = () => {
    navigate('/profil');
  };

  const handleResetCategory = () => {
    setSelectedCategory(null);
  };

  return (
    <div className="main-page fade-in">
      {/* Background */}
      <div className="main-background"></div>

      {/* Header Section */}
      <div className="main-header">
        <div className="main-logo-container">
          <img src="/images/logo.png" alt="Wiin Logo" className="main-logo" />
        </div>
        <div className="main-profile-info">
          <div className="main-greeting-container">
            <h2 className="main-greeting">Hej {userName}!</h2>
            <img 
              src={userIconPath} 
              alt="User Avatar" 
              className="main-avatar" 
              onClick={handleProfileClick}
            />
          </div>
          <p className="main-profile-link" onClick={handleProfileClick}>Din profil <span className="main-arrow">➔</span></p>
        </div>
      </div>

      {/* Categories Slider */}
      <div className="main-categories-container">
        <div className="main-categories">
          {categories.map((category) => (
            <div 
              key={category.id} 
              className={`main-category-item ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => handleCategoryClick(category.name)}
              style={{ cursor: 'pointer' }}
            >
              <img src={category.image} alt={category.name} className="main-category-image" />
            </div>
          ))}
        </div>
      </div>

      {/* Empty State for Category with No Campaigns */}
      {!loading && selectedCategory && popularCampaigns.length === 0 && recommendedCampaigns.length === 0 ? (
        <div className="main-empty-state">
          <p className="main-empty-message">Der er desværre ingen aktiviteter i denne kategori - prøv venligst én anden</p>
          <button className="main-reset-button" onClick={handleResetCategory}>
            Tilbage til alle aktiviteter
          </button>
        </div>
      ) : (
        <>
          {/* Popular Content Section */}
          <div className="main-section">
            <div className="main-section-header">
              <h3 className="main-section-title">Populære</h3>
              <button className="main-view-all" onClick={() => {}}>Se alle</button>
            </div>
            
            {loading ? (
              <div className="main-loading">
                <div className="spinner"></div>
                <p>Indlæser kampagner...</p>
              </div>
            ) : (
              <div className="main-popular-content">
                {popularCampaigns.map((campaign) => (
                  <div key={campaign.id} className="main-content-item">
                    <div className="main-image-container">
                      <img src={campaign.imageUrl} alt={campaign.title} className="main-content-image" />
                      {campaign.isCompleted && (
                        <div className="main-completed-badge">
                          <span className="main-checkmark">✓</span>
                        </div>
                      )}
                    </div>
                    <div className="main-campaign-info">
                      <img src={campaign.brandLogo} alt={campaign.brandName} className="main-brand-logo" />
                      <h4 className="main-campaign-title">{campaign.title}</h4>
                      <p className="main-campaign-description">{campaign.description}</p>
                    </div>
                    <button className="main-start-button" onClick={() => handleStartGame(campaign.id)}>Start spil</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recommended Content Section */}
          <div className="main-section">
            <div className="main-section-header">
              <h3 className="main-section-title">Anbefalet</h3>
            </div>
            
            {loading ? (
              <div className="main-loading">
                <div className="spinner"></div>
                <p>Indlæser kampagner...</p>
              </div>
            ) : (
              <div className="main-bottom-content">
                {recommendedCampaigns.map((campaign) => (
                  <div key={campaign.id} className="main-bottom-item">
                    <div className="main-image-container">
                      <img src={campaign.imageUrl} alt={campaign.title} className="main-bottom-image" />
                      {campaign.isCompleted && (
                        <div className="main-completed-badge">
                          <span className="main-checkmark">✓</span>
                        </div>
                      )}
                    </div>
                    <div className="main-campaign-info">
                      <img src={campaign.brandLogo} alt={campaign.brandName} className="main-brand-logo-small" />
                      <h4 className="main-campaign-title-small">{campaign.title}</h4>
                    </div>
                    <button className="main-start-button" onClick={() => handleStartGame(campaign.id)}>Start spil</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Footer Navigation */}
      <Footer />

      {/* Notifications */}
      {showFriendRequestNotification && (
        <FriendRequestNotification
          requests={friendRequests}
          onClose={handleCloseFriendRequestNotification}
          onUpdate={handleFriendRequestUpdate}
        />
      )}

      {showPrizeNotification && (
        <PrizeReceivedNotification
          notifications={prizeNotifications}
          onClose={handleClosePrizeNotification}
        />
      )}
    </div>
  );
};

export default MainPage; 